<template>
    <validation-observer ref="masterValidationObserver" v-slot="{ invalid, errors }">
        <v-container fluid class="pa-3" v-if="sessionActive">

            <v-card color="grey darken-3" height="100%" class="pt-0 pl-0">
                <material-header
                        title="Panel settings"
                        info="Global settings"
                        icon="mdi-cog"
                        :loading="settingsLoading"
                >

                    <template slot="buttons">

                        <v-tooltip top v-if="hasPermission(minPermission)">
                            <template v-slot:activator="{ on }">
                                <v-btn @click="refreshClicked()" v-on="on" icon>
                                    <v-icon>mdi-refresh</v-icon>
                                </v-btn>
                            </template>
                            <span>Reload data</span>
                        </v-tooltip>

                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn :disabled="!hasPermission('update panel settings')"
                                       v-on="on"
                                       color="primary"
                                       :loading="settingsSaving"
                                       @click="saveSettingsClicked()">
                                    SAVE
                                </v-btn>
                            </template>
                            <span v-if="hasPermission('update panel settings')">Save panel settings</span>
                            <span v-else>You don't have permission to update panel settings</span>
                        </v-tooltip>

                    </template>
                </material-header>

                <v-alert v-if="!hasPermission(minPermission)"
                         class="body-2"
                         type="error"
                >
                    Your user account doesn't have sufficient permissions!
                </v-alert>

                <v-tabs v-if="hasPermission(minPermission)"
                        v-model="activeTab"
                        dark
                        slider-color="primary"
                        color="white"
                >

                    <v-tab class="ml-3" key="tab1">Basic settings</v-tab>
                    <v-tab key="tab2">Business rules</v-tab>
                    <v-tab key="tab3">Compensation</v-tab>
                    <v-tab key="tab4">Integrations</v-tab>
                    <v-tab key="tab5">API Clients</v-tab>

                    <v-tab-item key="tab1">
                        <panel-settings
                                :settings="settings"
                                :error-messages="errors"
                                :currencies="currencies"
                                @change="settingsChanged"
                        ></panel-settings>
                    </v-tab-item>

                    <!-- BUSINESS RULES TAB -->
                    <v-tab-item key="tab2">
                        <business-rules-tab
                                :json-filter="form.data.business_rules_json_filter"
                                :json-query="form.data.business_rules_json_query"
                                :routing-enabled="form.data.routing_enabled"
                                :max-routing-attempts="form.data.max_routing_attempts"
                                @change="businessRulesUpdated"
                        ></business-rules-tab>
                    </v-tab-item>

                    <!-- COMPENSATION TAB -->
                    <v-tab-item key="tab3">
                        <compensation-tab class="mb-5"
                                          :reward-completed="form.data.reward_completed"
                                          :reward-profile="form.data.reward_profile"
                                          :reward-quota="form.data.reward_quota"
                                          :revenue-completed="form.data.revenue_completed"
                                          :revenue-profile="form.data.revenue_profile"
                                          :revenue-quota="form.data.revenue_quota"
                                          @change="compensationChanged"
                        ></compensation-tab>
                    </v-tab-item>

                    <!-- INTEGRATIONS -->
                    <v-tab-item key="tab4">
                        <integrations-tab></integrations-tab>
                    </v-tab-item>

                    <!-- API -->
                    <v-tab-item key="tab5" :disabled="!hasRole('Admin')">
                        <api-tab></api-tab>
                    </v-tab-item>

                </v-tabs>
            </v-card>

            <confirmation-dialog v-model="confirmationDialogModel"
                                 confirm-button-text="Proceed"
                                 headline="Information loss"
                                 description="You will loose any changes you have made if you proceed reloading panel settings"
                                 @selected="doRefresh"/>

            <json v-if="false" :data="form.data"></json>

        </v-container>
    </validation-observer>
</template>

<script>

    import Store from '../store';
    import {mapState, mapGetters, mapActions} from 'vuex';
    import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
    import MaterialHeader from '../../../components/UI/MaterialHeader';
    import BusinessRulesTab from './BusinessRulesTab';
    import PanelSettings from './BasicSettingsTab';
    import Form from '../../../components/forms/Form';
    import CompensationTab from "./CompensationTab";
    import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";
    import ApiTab from "./ApiTab";
    import IntegrationsTab from "./IntegrationsTab";

    import Json from 'vue-json-pretty';

    const vuexModule = 'PanelSettings';

    export default {

        mixins: [activeSessionChecker],

        components: {
            CompensationTab,
            MaterialHeader,
            BusinessRulesTab,
            PanelSettings,
            ApiTab,
            Json,
            ConfirmationDialog,
            IntegrationsTab
        },

        data() {
            return {
                minPermission: 'view panel settings',
                activeTab: 0,
                form: new Form([]),
                confirmationDialogModel: false,
                unsavedSettings: false,
            }
        },
        computed: {

            ...mapState(vuexModule, [
                'settings',
                'settingsLoading',
                'settingsSaving'
            ]),

            ...mapGetters(vuexModule, []),

            errors() {
                return this.form.getAllErrors();
            },

            currencies() {
                if (this.settings.locales) {
                    return this.settings.locales.map(locale => locale.currency);
                } else {
                    return [];
                }
            }
        },

        methods: {
            ...mapActions([
                'getDataVariables'
            ]),
            ...mapActions(vuexModule, [
                'getSettings',
                'saveSettings',
            ]),

            refreshClicked() {
                if (this.unsavedSettings) {
                    this.confirmationDialogModel = true;
                } else {
                    this.doRefresh(true);
                }
            },

            doRefresh(confirmed) {
                if (confirmed) {
                    this.getSettings(true)
                        .then(() => {
                            Bus.$emit('flash-message', {message: 'Panel settings reloaded!'});
                        })
                        .catch(() => {
                            Bus.$emit('server-error');
                        });
                }
            },

            saveSettingsClicked() {
                this.$refs.masterValidationObserver.validate()
                    .then(isValid => {
                        if (isValid) {

                            this.saveSettings(this.form)
                                .then(() => {
                                    Bus.$emit('flash-message', {message: 'Panel settings saved!'});
                                    this.unsavedSettings = false;
                                })
                                .catch((response) => {

                                    if (response.status === 422) {
                                        Bus.$emit('flash-message', {
                                            type: 'error',
                                            message: 'Panel settings contain errors!'
                                        });
                                    } else {
                                        Bus.$emit('server-error');
                                    }
                                });
                        } else {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: 'Panel settings contain errors!'
                            });
                        }
                    });

            },

            settingsChanged(settings) {
                this.unsavedSettings = true;
                this.form.data = Object.assign({}, this.form.data, settings);
            },

            compensationChanged(settings) {
                this.unsavedSettings = true;
                this.form.data = Object.assign(this.form.data, settings);
            },

            businessRulesUpdated(settings) {
                this.unsavedSettings = true;
                this.form.data = Object.assign(this.form.data, settings);
            },
        },

        created() {
            if (!this.$store.state[vuexModule]) {
                this.$store.registerModule(vuexModule, Store);
            }

            // Data variables must be loaded
            this.getDataVariables();
        },
    }

</script>
